import { capitalize } from 'lodash-es'

import { FvLinkButton, Icon } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'
import { fvDateUtc } from '@fv/dates'

import InfoBox from '@/components/InfoBox'
import { List, ListItem, ListItemActions } from '@/components/List'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import { type LabeledDocument, useLabeledDocuments } from '@/hooks/documents'
import { composeFvBolUrl } from '@/utils/shipmentFuncs'

import DeleteDocumentButton from './DeleteDocumentButton'

type DocumentListProps = Pick<FullShipment, 'documents'> & {
  className?: string
  loadId?: string
  canDelete?: boolean
  onClick?: (doc: LabeledDocument) => void
}

const DocumentList = ({
  documents,
  className,
  loadId,
  onClick,
  canDelete = false,
}: DocumentListProps) => {
  const fvBolUrl = composeFvBolUrl({ documents })
  const docs = useLabeledDocuments(documents)

  return (
    <List className={className}>
      {docs.map((doc, index) => (
        <ListItem
          custom
          key={doc._id ?? index}
          {...(doc.source === 'shipper' &&
            canDelete &&
            loadId && {
              actions: (
                <ListItemActions>
                  <DeleteDocumentButton documentId={doc._id} loadId={loadId} />
                </ListItemActions>
              ),
            })}
        >
          <div className="flex-1 overflow-hidden flex items-center">
            <FvLinkButton
              icon="file"
              className="p-[.6rem] pl-0 text-left block truncate"
              {...(!!onClick && { onClick: () => onClick(doc) })}
              {...(!onClick && {
                href: doc.label === 'Freightview BOL' ? fvBolUrl : doc.url,
                rel: 'noreferrer',
                target: '_blank',
              })}
            >
              {doc.label}
            </FvLinkButton>
            {doc.docCenterSource && (
              <ShipperTooltip label="This document was uploaded via the document center.">
                <Icon icon="server" />
              </ShipperTooltip>
            )}
            <span className="text-fv-gray-dark text-xs ">
              {generateTooltipLabel(doc)}
            </span>
          </div>
        </ListItem>
      ))}

      {!documents?.length && (
        <InfoBox>No documents have been uploaded.</InfoBox>
      )}
    </List>
  )
}

const added = 'added'
const generateTooltipLabel = (doc: LabeledDocument) => {
  if (!doc.uploadDate) return ''

  const source = doc.source === 'carrier' ? capitalize(doc.source) : ''
  const verb = !source ? capitalize(added) : added
  return `${source} ${verb} ${fvDateUtc(doc.uploadDate)
    .local()
    .format('MM/DD/YYYY')}`
}
export default DocumentList
